import React from 'react';
import { Link } from 'react-router-dom';
import buildingImg from '../assets/building.png';
import SEO from '../components/SEO';

const OurPresence = () => {
  // Expanded data array to include all Indian States + Delhi NCR
  const states = [
    { name: 'ANDHRA PRADESH', desc: 'Value-driven projects that enhance lifestyles across Andhra Pradesh.' },
    { name: 'ARUNACHAL PRADESH', desc: 'Expanding modern infrastructure amidst serene and pristine landscapes.' },
    { name: 'ASSAM', desc: 'Pioneering premium residential and commercial hubs in the Northeast.' },
    { name: 'BIHAR', desc: 'Transforming urban living with state-of-the-art modern developments.' },
    { name: 'CHHATTISGARH', desc: 'Delivering sustainable, integrated townships and commercial centers.' },
    { name: 'DELHI NCR', desc: 'Transforming the capital region with ultra-luxury residential and commercial spaces.' },
    { name: 'GOA', desc: 'Creating exclusive coastal retreats, premium villas, and luxury holiday homes.' },
    { name: 'GUJARAT', desc: 'Pioneering modern infrastructure in rapidly growing business and trade hubs.' },
    { name: 'HARYANA', desc: 'Developing futuristic tech-parks and premium residential communities.' },
    { name: 'HIMACHAL PRADESH', desc: 'Building eco-friendly luxury estates and retreats in the hills.' },
    { name: 'JHARKHAND', desc: 'Driving urban growth with contemporary commercial and residential projects.' },
    { name: 'KARNATAKA', desc: 'Delivering iconic residential and commercial projects across Karnataka.' },
    { name: 'KERALA', desc: 'Premium projects across major cities and emerging locations in Kerala.' },
    { name: 'MADHYA PRADESH', desc: 'Setting new benchmarks for modern, luxurious living in central India.' },
    { name: 'MAHARASHTRA', desc: 'Strategic investments and premium developments in the financial capital.' },
    { name: 'MANIPUR', desc: 'Cultivating modern community living spaces with premium amenities.' },
    { name: 'MEGHALAYA', desc: 'Blending nature with premium architectural design and sustainability.' },
    { name: 'MIZORAM', desc: 'Elevating residential standards with innovative and secure designs.' },
    { name: 'NAGALAND', desc: 'Crafting secure, modern, and aesthetically pleasing environments for communities.' },
    { name: 'ODISHA', desc: 'Fostering economic growth with state-of-the-art commercial and IT hubs.' },
    { name: 'PUNJAB', desc: 'Bringing ultra-luxury living and vibrant commercial centers to the forefront.' },
    { name: 'RAJASTHAN', desc: 'Merging heritage-inspired architecture with modern ultra-luxury amenities.' },
    { name: 'SIKKIM', desc: 'Creating sustainable, scenic luxury developments integrated with nature.' },
    { name: 'TAMIL NADU', desc: 'Creating world-class landmarks in prime locations of Tamil Nadu.' },
    { name: 'TELANGANA', desc: 'Building the future with innovative and sustainable developments.' },
    { name: 'TRIPURA', desc: 'Enhancing urban infrastructure with high-quality real estate solutions.' },
    { name: 'UTTAR PRADESH', desc: 'Redefining skylines with massive residential and IT corporate corridors.' },
    { name: 'UTTARAKHAND', desc: 'Serene luxury villas and wellness retreats nestled in the lap of nature.' },
    { name: 'WEST BENGAL', desc: 'Delivering architectural brilliance and premium lifestyle spaces in the cultural capital.' },
  ];

  return (
    <div className="bg-white min-h-screen w-full font-sans">
      <SEO 
        title="Global Presence & Markets | HBB0011 REALTY"
        description="Explore HBB0011 REALTY's global footprint. We bring our legacy of luxury corporate architecture to prestigious markets worldwide."
        keywords="global real estate markets, international properties, HBB0011 REALTY locations"
      />
      
      {/* 1. Hero Section */}
      <section className="relative w-full pt-28 pb-16 md:pt-32 md:pb-24 lg:min-h-[80vh] bg-[#041424] overflow-hidden flex items-center border-b border-gray-800">
        
        {/* Background Map Graphic (Placeholder) */}
        <div className="absolute inset-0 z-0 flex justify-end items-center opacity-30 lg:opacity-50 mix-blend-screen pointer-events-none">
          <div className="w-full lg:w-2/3 h-full relative">
            <img 
              src={buildingImg} 
              alt="India Map Concept" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#041424] via-[#041424]/80 lg:via-[#041424]/40 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#041424] via-transparent to-transparent"></div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left: Text Content */}
            <div className="lg:col-span-7">
              {/* Breadcrumbs */}
              <div className="flex items-center text-xs sm:text-sm font-bold tracking-widest mb-6 gap-2">
                <Link to="/" className="text-white hover:text-[#D4AF37] transition-colors uppercase">Home</Link>
                <span className="text-gray-400 material-symbols-outlined text-sm">chevron_right</span>
                <span className="text-[#D4AF37] uppercase">Locations</span>
              </div>

              <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6 leading-tight tracking-tight uppercase">
                Our Presence <br/>
                <span className="text-[#D4AF37]">Across India</span>
              </h1>
              
              <h2 className="text-xl sm:text-2xl text-white font-medium mb-6 leading-relaxed">
                Building Landmarks.<br/>
                Creating Value Everywhere.
              </h2>
              
              <p className="text-sm sm:text-base text-gray-400 mb-12 max-w-xl leading-relaxed">
                HBB0011 Realty Realtors Pvt. Ltd. is proudly building iconic projects and delivering premium real estate solutions across all states in India.
              </p>

              {/* Hero Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-4">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">location_on</span>
                  <div>
                    <div className="text-white font-bold text-xl">28</div>
                    <div className="text-gray-400 text-[9px] uppercase tracking-widest mt-1">States</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">domain</span>
                  <div>
                    <div className="text-white font-bold text-xl">25+</div>
                    <div className="text-gray-400 text-[9px] uppercase tracking-widest mt-1">Landmarks</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">public</span>
                  <div>
                    <div className="text-white font-bold text-xl">Pan</div>
                    <div className="text-gray-400 text-[9px] uppercase tracking-widest mt-1">India</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">groups</span>
                  <div>
                    <div className="text-white font-bold text-xl">5000+</div>
                    <div className="text-gray-400 text-[9px] uppercase tracking-widest mt-1">Happy Clients</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 2. Stats Banner */}
      <section className="bg-[#06121e] py-12 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-8">
            
            {/* Left Text */}
            <div className="lg:w-1/3 text-center lg:text-left">
              <h3 className="text-[#D4AF37] text-lg sm:text-xl font-bold tracking-widest uppercase mb-3 leading-snug">
                Expanding Horizons.<br/>Delivering Excellence.
              </h3>
              <p className="text-gray-400 text-xs leading-relaxed max-w-sm mx-auto lg:mx-0">
                From South to West, East to North - our commitment to quality and trust knows no boundaries.
              </p>
            </div>

            {/* Right Stats Grid */}
            <div className="lg:w-2/3 grid grid-cols-2 md:grid-cols-4 gap-6 divide-x divide-gray-800 w-full">
              <div className="px-2 flex flex-col items-center text-center">
                <span className="material-symbols-outlined text-[#D4AF37] text-4xl font-light mb-3">aspect_ratio</span>
                <p className="text-2xl sm:text-3xl font-bold text-white mb-1">2.5+</p>
                <p className="text-gray-500 text-[9px] uppercase tracking-widest">Lakh Sq. Ft.<br/>Total Development</p>
              </div>
              
              <div className="px-2 flex flex-col items-center text-center">
                <span className="material-symbols-outlined text-[#D4AF37] text-4xl font-light mb-3">construction</span>
                <p className="text-2xl sm:text-3xl font-bold text-white mb-1">20+</p>
                <p className="text-gray-500 text-[9px] uppercase tracking-widest">Ongoing<br/>Projects</p>
              </div>
              
              <div className="px-2 flex flex-col items-center text-center">
                <div className="w-10 h-10 rounded-full border border-[#D4AF37] text-[#D4AF37] flex items-center justify-center mb-3">
                  <span className="text-xl font-medium">₹</span>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-white mb-1">2000+</p>
                <p className="text-gray-500 text-[9px] uppercase tracking-widest">Crores Total<br/>Project Value</p>
              </div>
              
              <div className="px-2 flex flex-col items-center text-center">
                <span className="material-symbols-outlined text-[#D4AF37] text-4xl font-light mb-3">workspace_premium</span>
                <p className="text-2xl sm:text-3xl font-bold text-white mb-1">10+</p>
                <p className="text-gray-500 text-[9px] uppercase tracking-widest">Years Of<br/>Excellence</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 3. States Section Grid */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center mb-16">
            <h2 className="text-[#041424] text-xl font-bold uppercase tracking-widest bg-white inline-block px-10 py-4 rounded-sm shadow-md border border-gray-200">
              States We Operate In
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {states.map((state, index) => (
              <div key={index} className="bg-[#041424] rounded-sm overflow-hidden shadow-xl flex flex-col group">
                <div className="relative h-48 overflow-hidden shrink-0">
                  <img src={buildingImg} alt={state.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-[#041424]/20 group-hover:bg-transparent transition-colors"></div>
                </div>
                <div className="p-6 flex-grow flex flex-col justify-between">
                  <div>
                    <h4 className="text-lg font-bold text-white mb-3 uppercase tracking-wider">{state.name}</h4>
                    <p className="text-gray-400 text-xs leading-relaxed mb-6">{state.desc}</p>
                  </div>
                  <Link to="#" className="text-[#D4AF37] text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 hover:text-white transition-colors mt-auto">
                    View Projects <span className="material-symbols-outlined text-sm">arrow_forward</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 4. Features Section */}
      <section className="bg-white py-16 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col lg:flex-row bg-white rounded-sm shadow-lg border border-gray-200 overflow-hidden">
            
            {/* Left Text Box */}
            <div className="lg:w-1/3 bg-gray-50 p-8 lg:p-12 border-b lg:border-b-0 lg:border-r border-gray-200 flex flex-col justify-center text-center lg:text-left">
              <h3 className="text-xl sm:text-2xl font-bold text-[#041424] mb-4 uppercase tracking-wide">Why These Locations?</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We choose locations that offer high growth potential, excellent connectivity, infrastructure development, and promising returns for our valued customers.
              </p>
            </div>
            
            {/* Right Features Grid */}
            <div className="lg:w-2/3 grid grid-cols-2 md:grid-cols-3 divide-x divide-y md:divide-y-0 divide-gray-200">
              
              <div className="p-6 sm:p-8 flex flex-col items-center justify-center text-center hover:bg-gray-50 transition-colors border-t border-gray-200 md:border-t-0">
                <span className="material-symbols-outlined text-[#041424] text-4xl font-light mb-4">my_location</span>
                <p className="text-xs font-bold text-[#041424] uppercase tracking-widest">Strategic<br/>Locations</p>
              </div>
              
              <div className="p-6 sm:p-8 flex flex-col items-center justify-center text-center hover:bg-gray-50 transition-colors border-t border-gray-200 md:border-t-0">
                <span className="material-symbols-outlined text-[#041424] text-4xl font-light mb-4">trending_up</span>
                <p className="text-xs font-bold text-[#041424] uppercase tracking-widest">High Growth<br/>Potential</p>
              </div>
              
              <div className="p-6 sm:p-8 flex flex-col items-center justify-center text-center hover:bg-gray-50 transition-colors border-t border-gray-200 md:border-t-0">
                <span className="material-symbols-outlined text-[#041424] text-4xl font-light mb-4">add_road</span>
                <p className="text-xs font-bold text-[#041424] uppercase tracking-widest">Excellent<br/>Connectivity</p>
              </div>
              
              <div className="p-6 sm:p-8 flex flex-col items-center justify-center text-center hover:bg-gray-50 transition-colors border-t border-gray-200">
                <span className="material-symbols-outlined text-[#041424] text-4xl font-light mb-4">corporate_fare</span>
                <p className="text-xs font-bold text-[#041424] uppercase tracking-widest">Infrastructure<br/>Development</p>
              </div>
              
              <div className="p-6 sm:p-8 flex flex-col items-center justify-center text-center hover:bg-gray-50 transition-colors border-t border-gray-200">
                <span className="material-symbols-outlined text-[#041424] text-4xl font-light mb-4">monetization_on</span>
                <p className="text-xs font-bold text-[#041424] uppercase tracking-widest">Better Returns on<br/>Investment</p>
              </div>
              
              <div className="p-6 sm:p-8 flex flex-col items-center justify-center text-center hover:bg-gray-50 transition-colors border-t border-gray-200">
                <span className="material-symbols-outlined text-[#041424] text-4xl font-light mb-4">groups</span>
                <p className="text-xs font-bold text-[#041424] uppercase tracking-widest">Improved Lifestyle<br/>& Communities</p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* 5. CTA Section */}
      <section className="bg-gray-50 py-16 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#041424] rounded-sm shadow-2xl overflow-hidden flex flex-col lg:flex-row relative">
            
            {/* Left Image Graphic */}
            <div className="lg:w-1/3 relative h-64 lg:h-auto border-b lg:border-b-0 lg:border-r border-gray-800">
              <img alt="HBB0011 Building" className="absolute inset-0 w-full h-full object-cover" src={buildingImg}/>
              <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-[#041424] via-[#041424]/80 to-transparent"></div>
            </div>
            
            {/* Middle Content */}
            <div className="lg:w-1/2 p-8 lg:p-12 z-10 flex flex-col justify-center text-center lg:text-left bg-[#041424]">
              <p className="text-white text-sm mb-2 font-medium tracking-wide">Let's Build the Future Together</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-light text-[#D4AF37] mb-8 leading-tight">
                EXPLORE INVESTMENT OPPORTUNITIES <br className="hidden lg:block"/><span className="font-bold">ACROSS INDIA</span>
              </h2>
              
              <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                <button className="inline-flex items-center justify-center px-6 py-4 rounded-sm text-[#041424] bg-[#D4AF37] hover:bg-white transition-colors font-bold text-[10px] sm:text-xs uppercase tracking-widest gap-2">
                  Explore Projects <span className="material-symbols-outlined text-sm">arrow_forward</span>
                </button>
                <button className="inline-flex items-center justify-center px-6 py-4 border border-gray-600 rounded-sm text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors font-bold text-[10px] sm:text-xs uppercase tracking-widest gap-2">
                  Talk to our Expert <span className="material-symbols-outlined text-sm">call</span>
                </button>
              </div>
            </div>
            
            {/* Right List */}
            <div className="lg:w-1/4 p-8 lg:p-10 z-10 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-gray-800 bg-[#06121e]">
              <ul className="space-y-8">
                <li className="flex items-center space-x-4">
                  <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light shrink-0">public</span>
                  <span className="text-xs font-bold text-white uppercase tracking-widest leading-relaxed">Pan India<br/>Presence</span>
                </li>
                <li className="flex items-center space-x-4">
                  <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light shrink-0">verified_user</span>
                  <span className="text-xs font-bold text-white uppercase tracking-widest leading-relaxed">Trusted by<br/>Thousands</span>
                </li>
                <li className="flex items-center space-x-4">
                  <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light shrink-0">military_tech</span>
                  <span className="text-xs font-bold text-white uppercase tracking-widest leading-relaxed">Committed to<br/>Excellence</span>
                </li>
              </ul>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default OurPresence;